import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Clock, MapPin, Users, Plus, CreditCard, 
  QrCode, CheckCircle, AlertCircle, Loader2, ArrowLeft, Trash2, 
  Building2, AlertTriangle, ChevronRight, Info, FileText, Search, UserCheck, Download, DollarSign
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import MemberLookup, { MemberMatchedBanner } from './MemberLookup';
import { useProtectedFetch } from '../hooks/useProtectedFetch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper functions for session data transformation
const getDayName = (dayOfWeek) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayOfWeek] || 'Unknown';
};

const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Calculate age from birthdate
// Calculate current age (as of today)
const calculateCurrentAge = (birthdate) => {
  if (!birthdate) return null;
  const today = new Date();
  const birth = new Date(birthdate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Get the age the child will be at end of 2026 (for term registration)
// This is used for age group determination
const calculateTermAge = (birthdate) => {
  if (!birthdate) return null;
  const birth = new Date(birthdate);
  // Age as of December 31, 2026
  return 2026 - birth.getFullYear();
};

// Legacy function for compatibility
const calculateAge = (birthdate) => calculateTermAge(birthdate);

// Get age group label based on term age
// A child turning 11 in 2026 should be U12
const getAgeGroupLabel = (age) => {
  if (age === null) return '';
  // Age group is always age + 1 (a 10-year-old plays U11, an 11-year-old plays U12)
  return `U${age + 1}`;
};

// Filter sessions for a specific child based on TERM age and gender
const filterSessionsForChild = (sessions, child) => {
  // Use term age (age as of Dec 31, 2026) for filtering
  const termAge = calculateTermAge(child.birthdate);
  const gender = child.gender;

  return sessions.filter(session => {
    // Filter by gender (if session has specific gender)
    if (session.gender && session.gender !== 'mixed') {
      if (session.gender !== gender) return false;
    }

    // Age filter — enforce EXACTLY ONE age group per child.
    // Portal returns overlapping ranges (e.g. U12 = min:10 max:12, U14 = min:12 max:14).
    // A 12-year-old fits both if max is inclusive → shows twice.
    // Correct interpretation: min_age inclusive, max_age EXCLUSIVE ("under X").
    // So termAge must satisfy: min_age <= age < max_age.
    if (session.minAge !== null && session.minAge !== undefined && termAge !== null) {
      if (termAge < session.minAge) return false;
    }
    if (session.maxAge !== null && session.maxAge !== undefined && termAge !== null) {
      if (termAge >= session.maxAge) return false;
    }
    return true;
  });
};

const AcademyRegistration = ({ onBack }) => {
  const protectedFetch = useProtectedFetch();
  // Steps: 1=Term, 2=Parent/Children, 3=Sessions, 4=Review, 5=Payment, 6=Confirmation
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Data from API
  const [terms, setTerms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [calculatedFees, setCalculatedFees] = useState(null);
  const [wantsDigitalMembership, setWantsDigitalMembership] = useState(false);
  
  // Quick Lookup State
  const [lookupNumber, setLookupNumber] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  
  // Parent Verification State
  const [parentVerified, setParentVerified] = useState(false);
  const [existingChildren, setExistingChildren] = useState([]);
  const [verifyingParent, setVerifyingParent] = useState(false);
  
  // Child Verification State
  const [childVerificationResult, setChildVerificationResult] = useState(null);
  const [verifyingChild, setVerifyingChild] = useState(false);
  const [pendingChildIndex, setPendingChildIndex] = useState(null);
  
  // Form state
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentAddress, setParentAddress] = useState('');
  // SCC Membership Number verification (via Portal /lookup-member).
  // Portal is authoritative — discount only applied when membership_verified: true.
  const [isSccMember, setIsSccMember] = useState(false);
  const [sccMembershipNumber, setSccMembershipNumber] = useState('');
  const [membershipVerified, setMembershipVerified] = useState(null); // null | true | false
  const [membershipLookupLoading, setMembershipLookupLoading] = useState(false);
  const [membershipLookupResult, setMembershipLookupResult] = useState(null); // {found, player, parent}
  
  const [children, setChildren] = useState([{
    name: '',
    birthdate: '',
    gender: '',
    emergency_contact: '',
    emergency_relationship: 'Parent',
    medical_info: '',
    selected_session_ids: [],
    existing_player_id: null,
    membership_number: null,
    nric_last4: ''
  }]);
  
  // Payment state
  const [registrationId, setRegistrationId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [transactionReference, setTransactionReference] = useState('');
  const [sccAccountNumber, setSccAccountNumber] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('pending');
  
  // Consent state
  const [consentTexts, setConsentTexts] = useState(null);
  const [consents, setConsents] = useState({
    pdpa: false,
    safe_sport: false,
    indemnity: false
  });
  
  // Membership numbers from portal
  const [membershipNumbers, setMembershipNumbers] = useState([]);
  
  // QR Code ref for download
  const qrCodeRef = useRef(null);
  
  // Download QR Code function
  const downloadQRCode = () => {
    const svg = qrCodeRef.current?.querySelector('svg');
    if (!svg) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    canvas.width = 300;
    canvas.height = 380;
    
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const qrSize = 200;
      const qrX = (canvas.width - qrSize) / 2;
      ctx.drawImage(img, qrX, 20, qrSize, qrSize);
      
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('SCC Football Academy', canvas.width / 2, 245);
      
      ctx.font = '12px Arial';
      ctx.fillText('PayNow to: 201422384R', canvas.width / 2, 270);
      ctx.fillText(`Amount: $${calculatedFees?.grand_total?.toFixed(2) || '0'} SGD`, canvas.width / 2, 290);
      ctx.fillText(`Ref: ${registrationId || 'ACADEMY'}`, canvas.width / 2, 310);
      
      ctx.font = '10px Arial';
      ctx.fillStyle = '#666666';
      ctx.fillText('Football And More Limited', canvas.width / 2, 335);
      ctx.fillText('Scan with your banking app', canvas.width / 2, 353);
      
      const link = document.createElement('a');
      link.download = `PayNow-SCC-Academy-${registrationId || 'Reg'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // === MEMBER VERIFICATION FUNCTIONS ===
  
  // Unified member lookup handler (from MemberLookup component)
  const handleMemberLookupMatch = (parent, children, membershipNumber) => {
    if (parent) {
      setParentName(parent.name || '');
      setParentEmail(parent.email || '');
      setParentPhone(parent.phone || '');
      setParentAddress(parent.address || '');
      setParentVerified(true);
    }
    if (Array.isArray(children) && children.length > 0) {
      setExistingChildren(children);
    }
    // If membership-number lookup returned a single specific child, auto-seed
    // the children array with it (matches old behaviour of handleQuickLookup).
    if (membershipNumber && children && children.length === 1 && children[0].id) {
      setChildren([{
        name: children[0].name || '',
        birthdate: children[0].birthdate || '',
        gender: children[0].gender || '',
        emergency_contact: parent?.phone || '',
        emergency_relationship: 'Parent',
        medical_info: children[0].medical_info || '',
        selected_session_ids: [],
        existing_player_id: children[0].player_id || children[0].id,
        membership_number: membershipNumber,
        nric_last4: '',
      }]);
    }
  };

  // Quick lookup by membership number
  const handleQuickLookup = async () => {
    if (!lookupNumber.trim()) {
      setLookupError('Please enter a membership number');
      return;
    }
    
    setLookupLoading(true);
    setLookupError('');
    
    try {
      const response = await protectedFetch(`${API_URL}/api/academy/lookup-member/${lookupNumber.trim()}`);
      
      if (response.ok) {
        const data = await response.json();
        // Pre-fill the form with member data
        if (data.parent) {
          setParentName(data.parent.name || '');
          setParentEmail(data.parent.email || '');
          setParentPhone(data.parent.phone || '');
          setParentAddress(data.parent.address || '');
          setParentVerified(true);
        }
        if (data.child) {
          setChildren([{
            name: data.child.name,
            birthdate: data.child.birthdate,
            gender: data.child.gender,
            emergency_contact: parentPhone,
            emergency_relationship: 'Parent',
            medical_info: data.child.medical_info || '',
            selected_session_ids: [],
            existing_player_id: data.child.id,
            membership_number: lookupNumber.trim(),
            nric_last4: ''
          }]);
        }
        if (data.children && data.children.length > 0) {
          setExistingChildren(data.children);
        }
        setLookupError('');
      } else if (response.status === 404) {
        setLookupError('Member not found. Please check the number or register as new.');
      } else {
        setLookupError('Lookup failed. Please try again.');
      }
    } catch (err) {
      setLookupError('Failed to connect. Please try again.');
    } finally {
      setLookupLoading(false);
    }
  };
  
  // Verify parent on email/phone blur
  const verifyParent = useCallback(async () => {
    if ((!parentEmail && !parentPhone) || verifyingParent) return;
    
    setVerifyingParent(true);
    
    try {
      const params = new URLSearchParams();
      if (parentEmail) params.append('email', parentEmail);
      if (parentPhone) params.append('phone', parentPhone);
      if (parentName) params.append('name', parentName);
      
      const response = await protectedFetch(`${API_URL}/api/academy/verify-parent?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        // Portal returns matches[] array; flatten the first match
        const firstMatch = Array.isArray(data?.matches) && data.matches.length > 0 ? data.matches[0] : null;
        const matchedParent = firstMatch?.parent || null;
        const matchedKids = firstMatch?.children || [];
        if (data.found && matchedKids.length > 0) {
          setParentVerified(true);
          setExistingChildren(matchedKids);
          if (!parentName && matchedParent?.name) {
            setParentName(matchedParent.name);
          }
        } else if (data.found && matchedParent) {
          setParentVerified(true);
          setExistingChildren([]);
        } else {
          setExistingChildren([]);
        }
      }
    } catch (err) {
      console.error('Parent verification failed:', err);
    } finally {
      setVerifyingParent(false);
    }
  }, [parentEmail, parentPhone, parentName, verifyingParent]);
  
  // Verify child before adding (check for duplicates)
  const verifyChildDuplicate = async (childIndex) => {
    const child = children[childIndex];
    if (!child.name || !child.birthdate) return;
    
    setVerifyingChild(true);
    setPendingChildIndex(childIndex);
    
    try {
      const response = await protectedFetch(`${API_URL}/api/academy/verify-child`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          child_name: child.name,
          child_birthdate: child.birthdate,
          nric_last4: child.nric_last4 || null,
          parent_email: parentEmail,
          parent_phone: parentPhone,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.found && data.possible_match) {
          setChildVerificationResult(data.possible_match);
        } else {
          setChildVerificationResult(null);
        }
      }
    } catch (err) {
      console.error('Child verification failed:', err);
    } finally {
      setVerifyingChild(false);
    }
  };
  
  // Select existing child from verification
  const selectExistingChild = (existingChild) => {
    if (pendingChildIndex !== null) {
      const updatedChildren = [...children];
      updatedChildren[pendingChildIndex] = {
        ...updatedChildren[pendingChildIndex],
        name: existingChild.name,
        birthdate: existingChild.birthdate,
        gender: existingChild.gender,
        medical_info: existingChild.medical_info || '',
        existing_player_id: existingChild.id,
        membership_number: existingChild.membership_number,
      };
      setChildren(updatedChildren);
    }
    setChildVerificationResult(null);
    setPendingChildIndex(null);
  };
  
  // Add existing child from parent's children list
  const addExistingChild = (existingChild) => {
    const alreadyAdded = children.some(c => c.existing_player_id === existingChild.id);
    if (alreadyAdded) return;
    
    const newChild = {
      name: existingChild.name,
      birthdate: existingChild.birthdate,
      gender: existingChild.gender,
      emergency_contact: parentPhone,
      emergency_relationship: 'Parent',
      medical_info: existingChild.medical_info || '',
      selected_session_ids: [],
      existing_player_id: existingChild.id,
      membership_number: existingChild.membership_number,
      nric_last4: ''
    };
    
    if (children.length === 1 && !children[0].name) {
      setChildren([newChild]);
    } else {
      setChildren([...children, newChild]);
    }
  };

  // Fetch terms on mount
  useEffect(() => {
    fetchTerms();
    fetchConsentTexts();
  }, []);

  const fetchConsentTexts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/academy/consent-texts`);
      if (response.ok) {
        const data = await response.json();
        setConsentTexts(data);
      }
    } catch (err) {
      console.error('Failed to fetch consent texts:', err);
    }
  };

  // Fetch sessions when term is selected - we'll refetch per child with their details
  useEffect(() => {
    if (selectedTerm) {
      // Fetch all sessions initially (will be filtered per child in step 3)
      fetchSessions(selectedTerm.id);
    }
  }, [selectedTerm]);

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/academy/terms`);
      if (response.ok) {
        const data = await response.json();
        // Handle both {terms: [...]} and direct array responses
        const termsList = data.terms || (Array.isArray(data) ? data : [data]);
        setTerms(termsList);
      } else {
        // Mock data if API not available
        setTerms([{
          id: 'term-1-2026',
          name: 'Term 1 2026',
          start_date: '2026-01-13',
          end_date: '2026-03-28',
          status: 'active'
        }]);
      }
    } catch (err) {
      console.error('Failed to fetch terms:', err);
      setTerms([{
        id: 'term-1-2026',
        name: 'Term 1 2026',
        start_date: '2026-01-13',
        end_date: '2026-03-28',
        status: 'active'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async (termId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/academy/sessions/${termId}`);
      if (response.ok) {
        const data = await response.json();
        // Handle {templates: [...]} or {sessions: [...]} or direct array
        let sessionsList = data.templates || data.sessions || (Array.isArray(data) ? data : []);
        
        // Transform portal format to our display format
        sessionsList = sessionsList.map(s => ({
          id: s.id,
          day: s.day_name || getDayName(s.day_of_week),
          dayOfWeek: s.day_of_week,
          time: `${formatTime(s.start_time)} - ${formatTime(s.end_time)}`,
          startTime: s.start_time,
          location: s.location || 'Dempsey Field',
          type: s.description || s.session_type || 'Training',
          gender: s.gender || 'mixed',
          price: s.price,
          minAge: s.min_age,
          maxAge: s.max_age,
          ageGroupName: s.age_group_name
        }));
        
        setSessions(sessionsList);
      } else {
        // Fallback session data - matches centralized content
        setSessions([
          { id: 'dev-tue', day: 'Tuesday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Development', gender: 'mixed' },
          { id: 'dev-sun', day: 'Sunday', time: '8:00 AM - 9:30 AM', location: 'Padang', type: 'Development', gender: 'mixed' },
          { id: 'comp-mon', day: 'Monday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Competitive', gender: 'mixed' },
          { id: 'comp-tue', day: 'Tuesday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Competitive', gender: 'mixed' },
          { id: 'comp-wed', day: 'Wednesday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Competitive', gender: 'mixed' },
          { id: 'girls-tue', day: 'Tuesday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Girls', gender: 'female', note: 'Mixed session' },
          { id: 'girls-wed', day: 'Wednesday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Girls', gender: 'female', note: 'Girls only' },
          { id: 'sat-early', day: 'Saturday', time: '3:00 PM - 4:30 PM', location: 'Dempsey Field', type: 'Saturday Training', gender: 'mixed', note: 'U12, U14, U15' },
          { id: 'sat-late', day: 'Saturday', time: '5:00 PM - 6:30 PM', location: 'Dempsey Field', type: 'Saturday Training', gender: 'mixed', note: 'U7, U8, U9, U10' },
        ]);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      // Fallback session data - matches centralized content
      setSessions([
        { id: 'dev-tue', day: 'Tuesday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Development', gender: 'mixed' },
        { id: 'dev-sun', day: 'Sunday', time: '8:00 AM - 9:30 AM', location: 'Padang', type: 'Development', gender: 'mixed' },
        { id: 'comp-mon', day: 'Monday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Competitive', gender: 'mixed' },
        { id: 'comp-tue', day: 'Tuesday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Competitive', gender: 'mixed' },
        { id: 'comp-wed', day: 'Wednesday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Competitive', gender: 'mixed' },
        { id: 'girls-tue', day: 'Tuesday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Girls', gender: 'female', note: 'Mixed session' },
        { id: 'girls-wed', day: 'Wednesday', time: '5:30 PM - 7:00 PM', location: 'Dempsey Field', type: 'Girls', gender: 'female', note: 'Girls only' },
        { id: 'sat-early', day: 'Saturday', time: '3:00 PM - 4:30 PM', location: 'Dempsey Field', type: 'Saturday Training', gender: 'mixed', note: 'U12, U14, U15' },
        { id: 'sat-late', day: 'Saturday', time: '5:00 PM - 6:30 PM', location: 'Dempsey Field', type: 'Saturday Training', gender: 'mixed', note: 'U7, U8, U9, U10' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch sessions filtered by child's gender and birthdate (for age)
  const fetchFilteredSessionsForChild = async (termId, child) => {
    try {
      const params = new URLSearchParams({ term_id: termId });
      if (child.gender) {
        params.append('gender', child.gender);
      }
      if (child.birthdate) {
        params.append('child_birthdate', child.birthdate);
      }
      
      const response = await fetch(`${API_URL}/api/academy/sessions/${termId}?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        let sessionsList = data.templates || data.sessions || (Array.isArray(data) ? data : []);
        
        // Transform to display format
        return sessionsList.map(s => ({
          id: s.id,
          day: s.day_name || getDayName(s.day_of_week),
          dayOfWeek: s.day_of_week,
          time: `${formatTime(s.start_time)} - ${formatTime(s.end_time)}`,
          startTime: s.start_time,
          location: s.location || 'Dempsey Field',
          type: s.description || s.session_type || 'Training',
          gender: s.gender || 'mixed',
          price: s.price,
          minAge: s.min_age,
          maxAge: s.max_age,
          ageGroupName: s.age_group_name
        }));
      }
    } catch (err) {
      console.error('Failed to fetch filtered sessions:', err);
    }
    return null; // Return null to fall back to client-side filtering
  };

  // Central helper: builds the /calculate-fees payload and updates state.
  // Used by (1) initial calc when entering Review, (2) Digital Membership toggle,
  // (3) parent_declared_existing toggle, (4) SCC membership verification.
  const postCalculateFees = async (childrenList, wantsDM) => {
    if (!selectedTerm) return;
    setLoading(true);
    try {
      const body = {
        term_id: selectedTerm.id,
        parent_email: parentEmail || '',
        wants_digital_membership: wantsDM,
        children: childrenList.map(c => ({
          name: c.name,
          existing_player_id: c.existing_player_id || null,
          parent_declared_existing: !!c.parent_declared_existing,
          selected_session_ids: c.selected_session_ids,
        })),
      };
      // Only forward SCC membership fields when the parent ticked the box
      if (isSccMember && sccMembershipNumber.trim()) {
        body.membership_number = sccMembershipNumber.trim();
        body.membership_verified = membershipVerified === true;
      }
      const response = await fetch(`${API_URL}/api/academy/calculate-fees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const fees = await response.json();
        setCalculatedFees(fees);
      }
    } catch (err) {
      console.error('Failed to calculate fees:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateFees = async (overrideWantsDM = null) => {
    const wantsDM = overrideWantsDM !== null ? overrideWantsDM : wantsDigitalMembership;
    await postCalculateFees(children, wantsDM);
  };

  const toggleDigitalMembership = async (value) => {
    setWantsDigitalMembership(value);
    if (selectedTerm && children.length > 0) {
      await postCalculateFees(children, value);
    }
  };

  // Toggle "New player" checkbox on the review screen. When unchecked,
  // Portal will drop the $148 registration_fee from that child's line_items
  // and recompute subtotal + grand_total server-side.
  const toggleParentDeclaredExisting = async (childIndex, isNewPlayer) => {
    // isNewPlayer === true  → parent says NEW  → parent_declared_existing = false (fee applies)
    // isNewPlayer === false → parent says EXISTING → parent_declared_existing = true  (fee waived)
    const updated = [...children];
    updated[childIndex].parent_declared_existing = !isNewPlayer;
    setChildren(updated);
    await postCalculateFees(updated, wantsDigitalMembership);
  };

  // SCC Membership verification: hits Portal via /api/academy/lookup-member/{number}.
  // On success → membership_verified:true → Portal will apply member discount on next fee calc.
  // On not-found → membership_verified:false → Portal records the claim for admin audit; standard rate.
  const verifyMembershipNumber = async () => {
    const num = sccMembershipNumber.trim();
    if (!num) return;
    setMembershipLookupLoading(true);
    setMembershipLookupResult(null);
    try {
      const response = await protectedFetch(`${API_URL}/api/academy/lookup-member/${encodeURIComponent(num)}`);
      if (response.ok) {
        const data = await response.json();
        // Portal shape: { found: true|false, player: {...}, parent: {name,email} }
        const found = !!(data && data.found);
        setMembershipVerified(found);
        setMembershipLookupResult(data);
      } else if (response.status === 404) {
        setMembershipVerified(false);
        setMembershipLookupResult({ found: false });
      } else {
        setMembershipVerified(false);
        setMembershipLookupResult({ found: false, error: true });
      }
    } catch (err) {
      console.error('Membership lookup failed:', err);
      setMembershipVerified(false);
      setMembershipLookupResult({ found: false, error: true });
    } finally {
      setMembershipLookupLoading(false);
    }
  };

  // Reset membership verification whenever the box is unticked or number is edited
  const handleSccMemberToggle = (checked) => {
    setIsSccMember(checked);
    if (!checked) {
      setSccMembershipNumber('');
      setMembershipVerified(null);
      setMembershipLookupResult(null);
    }
  };

  const handleMembershipNumberChange = (value) => {
    setSccMembershipNumber(value);
    // Any edit invalidates the previous verification
    if (membershipVerified !== null) {
      setMembershipVerified(null);
      setMembershipLookupResult(null);
    }
  };

  const addChild = () => {
    setChildren([...children, {
      name: '',
      birthdate: '',
      gender: '',
      emergency_contact: parentPhone,
      emergency_relationship: 'Parent',
      medical_info: '',
      selected_session_ids: [],
      existing_player_id: null,
      parent_declared_existing: false,
      membership_number: null,
      nric_last4: ''
    }]);
  };

  const removeChild = (index) => {
    if (children.length > 1) {
      setChildren(children.filter((_, i) => i !== index));
    }
  };

  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index][field] = value;

    // Auto-link to existing player when the manually-typed name (+ optional DOB) matches
    // a child returned by MemberLookup for this parent. This prevents Portal from
    // charging the registration fee for a returning kid.
    // Only fires for rows the user is filling in fresh (no existing_player_id yet).
    if (!updated[index].existing_player_id && (field === 'name' || field === 'birthdate')) {
      const typedName = (updated[index].name || '').trim().toLowerCase();
      const typedDob = (updated[index].birthdate || '').trim();
      if (typedName.length >= 3 && existingChildren.length > 0) {
        const match = existingChildren.find(ec => {
          const ecName = (ec.name || '').trim().toLowerCase();
          const ecDob = (ec.birthdate || ec.dob || '').trim();
          if (ecName !== typedName) return false;
          // If DOB is present on both sides, require it to match too
          if (typedDob && ecDob && typedDob !== ecDob) return false;
          return true;
        });
        if (match) {
          updated[index].existing_player_id = match.player_id || match.id;
          updated[index].parent_declared_existing = false;
          if (!updated[index].birthdate && (match.birthdate || match.dob)) {
            updated[index].birthdate = match.birthdate || match.dob;
          }
          if (!updated[index].gender && match.gender) {
            updated[index].gender = match.gender;
          }
          if (match.membership_number) {
            updated[index].membership_number = match.membership_number;
          }
        }
      }
    }

    setChildren(updated);
  };

  const toggleSession = (childIndex, sessionId) => {
    const updated = [...children];
    const currentIds = updated[childIndex].selected_session_ids;

    // Un-selecting → always allowed
    if (currentIds.includes(sessionId)) {
      updated[childIndex].selected_session_ids = currentIds.filter(id => id !== sessionId);
      setChildren(updated);
      setError('');
      return;
    }

    // ── Rule 1: Enforce max_sessions_per_week cap (per Portal rate_table_snapshot)
    const maxPerWeek = selectedTerm?.rate_table_snapshot?.max_sessions_per_week
      ?? selectedTerm?.max_sessions_per_week
      ?? null;
    if (maxPerWeek && currentIds.length >= maxPerWeek) {
      setError(`This programme allows a maximum of ${maxPerWeek} session${maxPerWeek === 1 ? '' : 's'} per week. Please deselect one first.`);
      return;
    }

    // ── Rule 2: Prevent booking two sessions on the SAME day at the SAME time
    const target = sessions.find(s => s.id === sessionId);
    if (target) {
      const clash = currentIds
        .map(id => sessions.find(s => s.id === id))
        .find(s => s && s.dayOfWeek === target.dayOfWeek && s.startTime === target.startTime);
      if (clash) {
        setError(`Time slot conflict — this session overlaps with one you've already selected on the same day/time.`);
        return;
      }
    }

    // OK to add
    updated[childIndex].selected_session_ids = [...currentIds, sessionId];
    setChildren(updated);
    setError('');
  };

  const validateStep = (currentStep) => {
    setError('');
    
    if (currentStep === 1 && !selectedTerm) {
      setError('Please select a term');
      return false;
    }
    
    if (currentStep === 2) {
      if (!parentName || !parentEmail || !parentPhone) {
        setError('Please fill in all parent details');
        return false;
      }
      for (let i = 0; i < children.length; i++) {
        // Skip validation for existing players since their data is pre-filled
        if (children[i].existing_player_id) continue;
        
        if (!children[i].name || !children[i].birthdate || !children[i].gender) {
          setError(`Please fill in all required details for Child ${i + 1}`);
          return false;
        }
      }
    }
    
    if (currentStep === 3) {
      for (let i = 0; i < children.length; i++) {
        if (children[i].selected_session_ids.length === 0) {
          setError(`Please select at least one session for ${children[i].name || `Child ${i + 1}`}`);
          return false;
        }
      }
    }
    
    // Validate consents before submitting (step 4)
    if (currentStep === 4) {
      if (!consents.pdpa || !consents.safe_sport || !consents.indemnity) {
        setError('Please accept all consent and indemnity agreements to proceed');
        return false;
      }
    }
    
    return true;
  };

  const nextStep = async () => {
    if (!validateStep(step)) return;
    
    if (step === 3) {
      await calculateFees();
    }
    
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
    setError('');
  };

  const handleSubmitRegistration = async () => {
    // Validate consents before submission
    if (!validateStep(4)) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/academy/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          term_id: selectedTerm.id,
          parent: {
            name: parentName,
            email: parentEmail,
            phone: parentPhone,
            address: parentAddress
          },
          children: children,
          is_scc_member: isSccMember,
          membership_type: null,
          membership_number: isSccMember && sccMembershipNumber.trim() ? sccMembershipNumber.trim() : null,
          membership_verified: isSccMember && sccMembershipNumber.trim() ? membershipVerified === true : null,
          wants_digital_membership: wantsDigitalMembership,
          calculated_fees: calculatedFees,
          consents: consents
        })
      });

      const data = await response.json();

      if (response.ok && data.registration_id) {
        // Server may have recomputed the fee — surface the authoritative version
        // for the payment step (paranoid: never charge parents more than what the review showed).
        if (data.fees) {
          setCalculatedFees(data.fees);
        }
        setRegistrationId(data.registration_id);
        setStep(5);
      } else {
        setError(data.detail || 'Registration failed');
      }
    } catch (err) {
      setError('Failed to submit registration');
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async () => {
    setLoading(true);
    setError('');
    setPaymentMethod('stripe');
    
    try {
      const response = await fetch(`${API_URL}/api/academy/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registration_id: registrationId,
          origin_url: window.location.origin
        })
      });
      
      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err) {
      setError('Payment initialization failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePayNowConfirm = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/academy/confirm-paynow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registration_id: registrationId,
          transaction_reference: transactionReference
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPaymentStatus('pending_verification');
        if (data.membership_numbers && data.membership_numbers.length > 0) {
          setMembershipNumbers(data.membership_numbers);
        }
        setStep(6);
      }
    } catch (err) {
      setError('Failed to confirm payment');
    } finally {
      setLoading(false);
    }
  };

  const handleSccBillingConfirm = async () => {
    if (!sccAccountNumber.trim()) {
      setError('Please enter your SCC account number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/academy/confirm-scc-billing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registration_id: registrationId,
          scc_account_number: sccAccountNumber
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPaymentStatus('pending_billing');
        if (data.membership_numbers && data.membership_numbers.length > 0) {
          setMembershipNumbers(data.membership_numbers);
        }
        setStep(6);
      }
    } catch (err) {
      setError('Failed to submit billing request');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Select Term
  const renderTermSelection = () => (
    <div className="space-y-6">
      <Card className="bg-gradient-card gold-border">
        <CardHeader>
          <CardTitle className="text-xl text-primary">Select Term</CardTitle>
          <CardDescription>Choose the term you want to register for</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid gap-4">
              {terms.map((term) => {
                const regFee = term.rate_table_snapshot?.registration_fee;
                const hasRegFee = regFee?.enabled && regFee?.amount > 0;
                
                return (
                  <button
                    key={term.id}
                    onClick={() => setSelectedTerm(term)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      selectedTerm?.id === term.id
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`term-${term.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-card-foreground">{term.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {term.start_date} to {term.end_date}
                        </p>
                        {hasRegFee && (
                          <p className="text-xs text-yellow-400 mt-1">
                            Registration fee: ${regFee.amount} {regFee.includes_kit ? '(includes Academy Kit)' : ''}
                          </p>
                        )}
                      </div>
                      {selectedTerm?.id === term.id && (
                        <CheckCircle className="h-5 w-5 text-primary" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Comprehensive Pricing Info — REMOVED. All pricing now lives in Step 4 (Review),
          driven entirely by Portal's /calculate-fees response so we never mislead parents
          with stale hardcoded numbers. */}
    </div>
  );

  // Step 2: Parent & Children Details
  const renderDetailsForm = () => (
    <div className="space-y-6">
      {/* Unified Member Lookup — by membership #, email, or phone */}
      <MemberLookup onMatch={handleMemberLookupMatch} />

      {/* Parent Details */}
      <Card className="bg-gradient-card gold-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-primary">Parent / Guardian Details</CardTitle>
            {verifyingParent && (
              <Badge variant="outline" className="text-muted-foreground">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Checking...
              </Badge>
            )}
            {parentVerified && !verifyingParent && (
              <Badge className="bg-success/20 text-success border-0">
                <UserCheck className="h-3 w-3 mr-1" />
                Verified
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                placeholder="Enter your full name"
                className="mt-1"
                data-testid="parent-name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                onBlur={verifyParent}
                placeholder="your@email.com"
                className="mt-1"
                data-testid="parent-email"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Phone Number *</Label>
              <Input
                value={parentPhone}
                onChange={(e) => setParentPhone(e.target.value)}
                onBlur={verifyParent}
                placeholder="+65 9xxx xxxx"
                className="mt-1"
                data-testid="parent-phone"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={parentAddress}
                onChange={(e) => setParentAddress(e.target.value)}
                placeholder="Home address (optional)"
                className="mt-1"
              />
            </div>
          </div>

          {/* SCC Membership Number verification (optional) */}
          <div className="border-t border-border pt-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Checkbox
                id="scc-member"
                checked={isSccMember}
                onCheckedChange={handleSccMemberToggle}
                data-testid="scc-member-checkbox"
              />
              <Label htmlFor="scc-member" className="cursor-pointer">I am an SCC Member</Label>
            </div>
            {isSccMember && (
              <div className="space-y-2">
                <Label htmlFor="scc-membership-number" className="text-sm">Membership Number</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    id="scc-membership-number"
                    value={sccMembershipNumber}
                    onChange={(e) => handleMembershipNumberChange(e.target.value)}
                    placeholder="e.g. AB1234"
                    className="flex-1"
                    data-testid="scc-membership-number"
                  />
                  <Button
                    type="button"
                    onClick={verifyMembershipNumber}
                    disabled={!sccMembershipNumber.trim() || membershipLookupLoading}
                    className="sm:w-32"
                    data-testid="verify-membership-btn"
                  >
                    {membershipLookupLoading ? 'Checking…' : 'Verify'}
                  </Button>
                </div>
                {membershipVerified === true && (
                  <div className="p-2 rounded bg-green-500/10 border border-green-500/30 text-sm text-green-300" data-testid="membership-verified-badge">
                    ✓ SCC Member confirmed{membershipLookupResult?.parent?.name ? ` — ${membershipLookupResult.parent.name}` : ''}. Your member discount will be applied automatically.
                  </div>
                )}
                {membershipVerified === false && (
                  <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30 text-sm text-amber-300" data-testid="membership-unverified-notice">
                    ⚠ We couldn&apos;t verify this membership number. Standard rate applies for now — our team will verify at reception. You can continue with your registration.
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Not sure of your number? Continue without it — our team will confirm your member status at reception.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Existing Children Selection (if parent verified) */}
      {parentVerified && existingChildren.length > 0 && (
        <Card className="bg-gradient-to-br from-green-900/20 to-emerald-900/10 border-green-600/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-success" />
              <CardTitle className="text-lg text-success">Your Registered Children</CardTitle>
            </div>
            <CardDescription>Select children to register for this term</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {existingChildren.map((existingChild) => {
                const isSelected = children.some(c => c.existing_player_id === existingChild.id);
                return (
                  <div 
                    key={existingChild.id}
                    className={`p-4 rounded-lg border transition-all cursor-pointer ${
                      isSelected 
                        ? 'bg-success/20 border-success' 
                        : 'bg-charcoal border-border hover:border-success/50'
                    }`}
                    onClick={() => !isSelected && addExistingChild(existingChild)}
                    data-testid={`academy-existing-child-${existingChild.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={() => !isSelected && addExistingChild(existingChild)}
                          className="data-[state=checked]:bg-success"
                        />
                        <div>
                          <p className="font-medium text-card-foreground">
                            {existingChild.name}
                            <Badge className="ml-2 bg-primary/20 text-primary border-0 text-xs">
                              {existingChild.membership_number}
                            </Badge>
                          </p>
                          <p className="text-sm text-muted-foreground">
                            DOB: {existingChild.birthdate} • {existingChild.gender}
                          </p>
                        </div>
                      </div>
                      {isSelected && (
                        <CheckCircle className="h-5 w-5 text-success" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              variant="outline"
              onClick={addChild}
              className="w-full mt-4 border-dashed border-success/50 text-success hover:bg-success/10"
              data-testid="academy-add-new-child-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Child
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Child Verification Warning Modal */}
      {childVerificationResult && (
        <Card className="bg-gradient-to-br from-yellow-900/30 to-amber-900/20 border-yellow-600/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              <CardTitle className="text-lg text-yellow-400">Possible Existing Member Found</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-charcoal rounded-lg mb-4">
              <p className="font-medium text-card-foreground">
                {childVerificationResult.name}
                <Badge className="ml-2 bg-primary/20 text-primary border-0 text-xs">
                  {childVerificationResult.membership_number}
                </Badge>
              </p>
              <p className="text-sm text-muted-foreground">
                DOB: {childVerificationResult.birthdate}
              </p>
            </div>
            <p className="text-sm text-card-foreground mb-4">
              Is this the same child you&apos;re trying to register?
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => selectExistingChild(childVerificationResult)}
                className="flex-1 bg-success text-white hover:bg-success/90"
              >
                Yes - Use Existing
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setChildVerificationResult(null);
                  setPendingChildIndex(null);
                }}
                className="flex-1"
              >
                No - Create New
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Children */}
      {children.map((child, index) => (
        <Card key={index} className={`bg-gradient-card ${child.existing_player_id ? 'border-success/50' : 'gold-border'}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg text-primary">
                  Child {index + 1}
                </CardTitle>
                {child.membership_number && (
                  <Badge className="bg-success/20 text-success border-0">
                    {child.membership_number}
                  </Badge>
                )}
                {index > 0 && <Badge className="bg-green-600 text-xs">Sibling Discount</Badge>}
              </div>
              {children.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => removeChild(index)} className="text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* If existing player, show read-only info */}
            {child.existing_player_id ? (
              <div className="p-4 bg-charcoal rounded-lg">
                <div className="flex items-center gap-2 text-success mb-2">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Existing Member</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <span className="ml-2 text-card-foreground">{child.name}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">DOB:</span>
                    <span className="ml-2 text-card-foreground">{child.birthdate}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Gender:</span>
                    <span className="ml-2 text-card-foreground capitalize">{child.gender}</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={child.name}
                      onChange={(e) => updateChild(index, 'name', e.target.value)}
                      onBlur={() => child.name && child.birthdate && verifyChildDuplicate(index)}
                      placeholder="Child's name"
                      className="mt-1"
                      data-testid={`child-${index}-name`}
                    />
                  </div>
                  <div>
                    <Label>Date of Birth *</Label>
                    <Input
                      type="date"
                      value={child.birthdate}
                      onChange={(e) => updateChild(index, 'birthdate', e.target.value)}
                      onBlur={() => child.name && child.birthdate && verifyChildDuplicate(index)}
                      className="mt-1"
                      data-testid={`child-${index}-dob`}
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Gender *</Label>
                    <Select value={child.gender} onValueChange={(v) => updateChild(index, 'gender', v)}>
                      <SelectTrigger className="mt-1" data-testid={`child-${index}-gender`}>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>NRIC Last 4 Digits</Label>
                    <Input
                      value={child.nric_last4 || ''}
                      onChange={(e) => updateChild(index, 'nric_last4', e.target.value.toUpperCase())}
                      placeholder="e.g., 456A"
                      maxLength={4}
                      className="mt-1 uppercase"
                      data-testid={`child-${index}-nric`}
                    />
                    <p className="text-xs text-muted-foreground mt-1">For verification purposes</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Emergency Contact</Label>
                    <Input
                      value={child.emergency_contact}
                      onChange={(e) => updateChild(index, 'emergency_contact', e.target.value)}
                      placeholder="+65 9xxx xxxx"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Medical Info</Label>
                    <Input
                      value={child.medical_info}
                      onChange={(e) => updateChild(index, 'medical_info', e.target.value)}
                      placeholder="Allergies, conditions"
                      className="mt-1"
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Add Another Child - only show if not using existing children flow */}
      {(!parentVerified || existingChildren.length === 0) && (
        <Button
          variant="outline"
          onClick={addChild}
          className="w-full gold-border text-primary hover:bg-primary/10"
          data-testid="academy-add-child-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Another Child (Sibling Discount)
        </Button>
      )}
    </div>
  );

  // Step 3: Session Selection
  const renderSessionSelection = () => {
    // Group sessions by type/category based on Portal API session_type values
    const groupSessionsByType = (sessionList) => {
      const groups = {
        'Competitive': [],
        'Training / Development': [],
        'Social': [],
        'Games (JSSL/SYL)': [],
        'Other': []
      };
      
      sessionList.forEach(session => {
        // Portal API uses session_type field, but it may be mapped to 'type' in frontend
        const type = (session.session_type || session.type || '').toLowerCase();
        
        if (type === 'competitive') {
          groups['Competitive'].push(session);
        } else if (type === 'training' || type === 'development' || type === 'recreational') {
          groups['Training / Development'].push(session);
        } else if (type === 'social') {
          groups['Social'].push(session);
        } else if (type === 'game' || type === 'games' || type === 'jssl' || type === 'syl') {
          groups['Games (JSSL/SYL)'].push(session);
        } else {
          groups['Other'].push(session);
        }
      });
      
      return groups;
    };

    // Category styling with SCC colors
    const categoryStyles = {
      'Competitive': { 
        color: 'text-green-400', 
        bg: 'bg-green-500/10', 
        border: 'border-green-500/30', 
        icon: '⚡',
        desc: 'Higher challenge, league play, and match preparation'
      },
      'Training / Development': { 
        color: 'text-blue-400', 
        bg: 'bg-blue-500/10', 
        border: 'border-blue-500/30', 
        icon: '🌱',
        desc: 'Skill building, fundamentals, and technique development'
      },
      'Social': { 
        color: 'text-purple-400', 
        bg: 'bg-purple-500/10', 
        border: 'border-purple-500/30', 
        icon: '🤝',
        desc: 'Fun-focused sessions, friendly matches, and team bonding'
      },
      'Games (JSSL/SYL)': { 
        color: 'text-amber-400', 
        bg: 'bg-amber-500/10', 
        border: 'border-amber-500/30', 
        icon: '🏆',
        desc: 'League matches and competitions'
      },
      'Other': { 
        color: 'text-gray-400', 
        bg: 'bg-gray-500/10', 
        border: 'border-gray-500/30', 
        icon: '⚽',
        desc: 'Additional training sessions'
      }
    };

    return (
      <div className="space-y-6">
        {children.map((child, childIndex) => {
          const termAge = calculateTermAge(child.birthdate);
          const currentAge = calculateCurrentAge(child.birthdate);
          const filteredSessions = filterSessionsForChild(sessions, child);
          const groupedSessions = groupSessionsByType(filteredSessions);

          // Derive the actual age group label from the sessions the child qualifies for
          // (Portal returns e.g. min_age:12 max_age:14 → label "U14"). Falls back to the
          // formula if Portal doesn't provide a name.
          const eligibleGroupLabels = Array.from(new Set(
            filteredSessions.map(s => s.ageGroupName || (s.maxAge ? `U${s.maxAge}` : null)).filter(Boolean)
          ));
          const ageGroupLabel = eligibleGroupLabels.length === 1
            ? eligibleGroupLabels[0]
            : eligibleGroupLabels.length > 1
              ? eligibleGroupLabels.join(' / ')
              : getAgeGroupLabel(termAge); // last-resort fallback
          
          // Filter out empty groups
          const nonEmptyGroups = Object.entries(groupedSessions).filter(([_, sessions]) => sessions.length > 0);
          
          return (
            <Card key={childIndex} className="bg-gradient-card gold-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg text-primary">
                      {child.name || `Child ${childIndex + 1}`}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1 flex-wrap">
                      {termAge !== null && (
                        <>
                          <Badge variant="outline" className="text-xs">
                            {currentAge !== termAge 
                              ? `Currently ${currentAge}, turning ${termAge} in 2026` 
                              : `Age: ${termAge}`}
                          </Badge>
                          <Badge className="bg-primary/20 text-primary text-xs">
                            {ageGroupLabel}
                          </Badge>
                          <Badge variant="outline" className="text-xs capitalize">
                            {child.gender}
                          </Badge>
                        </>
                      )}
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Selected</p>
                    <p className="text-lg font-bold text-primary">
                      {child.selected_session_ids.length}
                      {selectedTerm?.rate_table_snapshot?.max_sessions_per_week && (
                        <span className="text-sm text-muted-foreground font-normal"> / {selectedTerm.rate_table_snapshot.max_sessions_per_week} max</span>
                      )}
                    </p>
                    {selectedTerm?.rate_table_snapshot?.max_sessions_per_week === 1 && (
                      <p className="text-xs text-muted-foreground mt-0.5">1 session/week only</p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredSessions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No sessions available for this age group and gender.</p>
                    <p className="text-sm mt-2">Please contact the academy for more information.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {nonEmptyGroups.map(([category, categorySessions]) => {
                      const style = categoryStyles[category] || categoryStyles['Other'];
                      const selectedCount = categorySessions.filter(s => child.selected_session_ids.includes(s.id)).length;
                      
                      return (
                        <div key={category} className={`rounded-xl border ${style.border} overflow-hidden`}>
                          {/* Category Header */}
                          <div className={`${style.bg} px-4 py-3 border-b ${style.border}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{style.icon}</span>
                                <h3 className={`font-semibold ${style.color}`}>{category}</h3>
                              </div>
                              {selectedCount > 0 && (
                                <Badge className={`${style.bg} ${style.color} border ${style.border}`}>
                                  {selectedCount} selected
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {style.desc}
                            </p>
                          </div>
                          
                          {/* Sessions Grid */}
                          <div className="p-3 grid gap-2 sm:grid-cols-2">
                            {categorySessions.map((session) => {
                              const isSelected = child.selected_session_ids.includes(session.id);
                              return (
                                <button
                                  key={session.id}
                                  data-testid={`session-${childIndex}-${session.id}`}
                                  onClick={() => toggleSession(childIndex, session.id)}
                                  className={`p-3 rounded-lg border text-left transition-all ${
                                    isSelected
                                      ? `border-primary bg-primary/10 ring-1 ring-primary/50`
                                      : 'border-border/50 hover:border-primary/50 bg-charcoal/30 hover:bg-charcoal/50'
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                          isSelected ? 'bg-primary' : 'bg-charcoal border border-border'
                                        }`} />
                                        <p className="font-medium text-card-foreground text-sm truncate">
                                          {session.day}
                                        </p>
                                      </div>
                                      <div className="ml-4.5 mt-1 space-y-1">
                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                          <Clock className="h-3 w-3" />
                                          {session.time}
                                        </p>
                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                          <MapPin className="h-3 w-3" />
                                          {session.location}
                                        </p>
                                        {session.note && (
                                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                            {session.note}
                                          </Badge>
                                        )}
                                        {session.ageGroupName && (
                                          <Badge className="bg-blue-500/20 text-blue-400 text-[10px] px-1.5 py-0">
                                            {session.ageGroupName}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    {isSelected && (
                                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  // Step 4: Review & Fees
  const renderReview = () => (
    <div className="space-y-6">
      <Card className="bg-gradient-card gold-border">
        <CardHeader>
          <CardTitle className="text-xl text-primary">Review Registration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-b border-border pb-4">
            <p className="text-sm text-muted-foreground">Term</p>
            <p className="text-card-foreground font-medium">{selectedTerm?.name}</p>
          </div>
          
          <div className="border-b border-border pb-4">
            <p className="text-sm text-muted-foreground">Parent</p>
            <p className="text-card-foreground">{parentName}</p>
            <p className="text-sm text-muted-foreground">{parentEmail}</p>
          </div>
          
          {/* Term meta — from Portal */}
          {calculatedFees?.term_meta && (
            <div className="border-b border-border pb-4" data-testid="fees-term-meta">
              <p className="font-medium text-card-foreground">{calculatedFees.term_meta.name}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {calculatedFees.term_meta.duration_weeks} weeks · {calculatedFees.term_meta.sessions_per_week_max} session/week ·{' '}
                {calculatedFees.term_meta.sessions_per_term_max} sessions total · ${calculatedFees.term_meta.price_per_session?.toFixed(2)}/session
              </p>
            </div>
          )}

          {/* Parent status banner from Portal */}
          {calculatedFees?.parent_status && calculatedFees.parent_status.detected_from === 'email_match' && (
            <div className="bg-green-500/10 border border-green-500/30 rounded p-2 text-sm text-green-300" data-testid="fees-parent-detected">
              Welcome back — {calculatedFees.parent_status.has_active_digital_membership
                ? `Digital Member (expires ${calculatedFees.parent_status.digital_membership_expires})`
                : (calculatedFees.parent_status.detected_member_type !== 'none'
                    ? `${calculatedFees.parent_status.detected_member_type} member`
                    : 'account found')}
            </div>
          )}

          {/* Per-child line items from Portal (render verbatim) */}
          {calculatedFees?.children_breakdown?.map((child, idx) => {
            const hasRegFee = child.line_items?.some(li => li.type === 'registration_fee');
            const childState = children[idx] || {};
            const isLinked = !!childState.existing_player_id;
            // Show override checkbox whenever this child has NOT been linked to a real player_id.
            // Parent can tick "New player" to keep the $148 fee (default) or untick to waive it
            // (parent_declared_existing → Portal drops the fee).
            const showNewPlayerCheckbox = !isLinked;
            const isNewPlayerChecked = !childState.parent_declared_existing;
            const showWaivedNotice = childState.parent_declared_existing && !hasRegFee;
            return (
            <div key={idx} className="border-b border-border pb-4" data-testid={`child-breakdown-${idx}`}>
              <div className="flex items-center justify-between">
                <p className="font-medium text-card-foreground">{child.name}</p>
                {child.is_new_player && (
                  <span className="text-xs text-yellow-400 bg-yellow-500/10 px-2 py-0.5 rounded">New player</span>
                )}
                {isLinked && (
                  <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded">Existing player</span>
                )}
              </div>
              <div className="mt-2 space-y-1">
                {child.line_items?.map((li, liIdx) => (
                  <div key={liIdx} className="flex justify-between text-sm">
                    <span className={li.type === 'discount' ? 'text-green-400' : 'text-muted-foreground'}>{li.label}</span>
                    <span className={li.type === 'discount' ? 'text-green-400' : 'text-card-foreground'}>
                      {li.amount < 0 ? '-' : ''}${Math.abs(li.amount).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Manual "New player" override — checked by default; parent unticks if child is already registered */}
              {showNewPlayerCheckbox && (
                <label
                  className="mt-3 flex items-start gap-3 cursor-pointer hover:bg-card/30 rounded p-2 -m-2 transition-colors"
                  data-testid={`new-player-toggle-wrapper-${idx}`}
                >
                  <input
                    type="checkbox"
                    checked={isNewPlayerChecked}
                    disabled={loading}
                    onChange={(e) => toggleParentDeclaredExisting(idx, e.target.checked)}
                    className="mt-1 accent-primary"
                    data-testid={`new-player-toggle-${idx}`}
                  />
                  <div className="flex-1 text-sm">
                    <p className="text-card-foreground font-medium">This is a new player at SCC Football Academy</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      New players include a one-time $148 Registration Fee (Academy Kit).
                      Untick if {child.name} has trained with the Academy before — our team will verify at reception.
                    </p>
                  </div>
                </label>
              )}

              {showWaivedNotice && (
                <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-300" data-testid={`waived-notice-${idx}`}>
                  Registration Fee waived — you&apos;ve indicated {child.name} is an existing player. Our team will verify this at reception.
                </div>
              )}

              <p className="text-primary font-bold mt-2 flex justify-between">
                <span>Subtotal</span>
                <span>${child.subtotal?.toFixed(2)}</span>
              </p>
            </div>
            );
          })}

          {/* Family line items (Digital Membership opt-in etc.) */}
          {calculatedFees?.family_line_items?.map((item, idx) => (
            <div key={idx} className="border-b border-border pb-4" data-testid={`family-item-${item.type}`}>
              {item.optional ? (
                <label className="flex items-start gap-3 cursor-pointer hover:bg-card/30 rounded p-2 -m-2 transition-colors">
                  <input
                    type="checkbox"
                    checked={wantsDigitalMembership}
                    onChange={(e) => toggleDigitalMembership(e.target.checked)}
                    className="mt-1 accent-primary"
                    data-testid={`toggle-${item.type}`}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start gap-3">
                      <p className="font-medium text-card-foreground">{item.label}</p>
                      <p className="font-bold text-primary whitespace-nowrap">
                        {item.opted_in
                          ? `+$${item.amount.toFixed(2)}`
                          : <span className="text-xs text-muted-foreground italic">Tick to add</span>}
                      </p>
                    </div>
                    {item.info && <p className="text-xs text-muted-foreground mt-1">{item.info}</p>}
                  </div>
                </label>
              ) : (
                <div className="flex justify-between text-sm">
                  <span className="text-card-foreground">{item.label}</span>
                  <span className="text-primary font-bold">${item.amount.toFixed(2)}</span>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Total */}
      <Card className="bg-charcoal border-primary">
        <CardContent className="p-6">
          <div className="flex justify-between items-center text-xl">
            <span className="text-card-foreground">Total Amount</span>
            <span className="text-primary font-bold" data-testid="grand-total">
              ${calculatedFees?.grand_total?.toFixed(2) || '0.00'} SGD
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Consent & Indemnity Section */}
      <Card className="bg-gradient-card gold-border">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg text-primary">Consent & Indemnity</CardTitle>
          </div>
          <CardDescription>Please read and accept the following agreements</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* PDPA Consent */}
          <div className="p-4 rounded-lg bg-charcoal border border-border">
            <div className="flex items-start gap-3">
              <Checkbox
                id="academy-consent-pdpa"
                checked={consents.pdpa}
                onCheckedChange={(checked) => setConsents(prev => ({ ...prev, pdpa: checked }))}
                className="mt-1"
                data-testid="academy-consent-pdpa"
              />
              <div className="flex-1">
                <Label htmlFor="academy-consent-pdpa" className="text-card-foreground font-medium cursor-pointer">
                  {consentTexts?.pdpa?.title || 'Personal Data Protection Act (PDPA) Consent'}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {consentTexts?.pdpa?.content || "I consent to the collection, use, and disclosure of my/my child's personal data by SCC Football Academy for the purposes of registration, communication, and administration of academy activities."}
                </p>
              </div>
            </div>
          </div>

          {/* Safe Sport Consent */}
          <div className="p-4 rounded-lg bg-charcoal border border-border">
            <div className="flex items-start gap-3">
              <Checkbox
                id="academy-consent-safe-sport"
                checked={consents.safe_sport}
                onCheckedChange={(checked) => setConsents(prev => ({ ...prev, safe_sport: checked }))}
                className="mt-1"
                data-testid="academy-consent-safe-sport"
              />
              <div className="flex-1">
                <Label htmlFor="academy-consent-safe-sport" className="text-card-foreground font-medium cursor-pointer">
                  {consentTexts?.safe_sport?.title || 'Safe Sport Commitment'}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {consentTexts?.safe_sport?.content || "I acknowledge and agree to abide by the SafeSport framework and guidelines. I understand my responsibilities in maintaining a safe sporting environment."}
                </p>
              </div>
            </div>
          </div>

          {/* Indemnity Consent */}
          <div className="p-4 rounded-lg bg-charcoal border border-border">
            <div className="flex items-start gap-3">
              <Checkbox
                id="academy-consent-indemnity"
                checked={consents.indemnity}
                onCheckedChange={(checked) => setConsents(prev => ({ ...prev, indemnity: checked }))}
                className="mt-1"
                data-testid="academy-consent-indemnity"
              />
              <div className="flex-1">
                <Label htmlFor="academy-consent-indemnity" className="text-card-foreground font-medium cursor-pointer">
                  {consentTexts?.indemnity?.title || 'Indemnity & Waiver'}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {consentTexts?.indemnity?.content || "I understand that participation in football activities involves inherent risks. I hereby release, waive, and indemnify SCC Football Academy, its coaches, staff, and volunteers from any claims arising from participation in academy activities."}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Step 5: Payment
  const renderPayment = () => (
    <div className="space-y-6">
      <Card className="bg-gradient-card gold-border">
        <CardHeader>
          <CardTitle className="text-xl text-primary">Select Payment Method</CardTitle>
          <CardDescription>
            Total: <span className="text-primary font-bold">
              ${calculatedFees?.grand_total?.toFixed(2)} SGD
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stripe */}
          <button
            onClick={handleStripePayment}
            disabled={loading}
            className="w-full p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors flex items-center gap-4"
            data-testid="stripe-btn"
          >
            <div className="h-12 w-12 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center">
              <CreditCard className="h-6 w-6 text-white" />
            </div>
            <div className="text-left flex-1">
              <p className="font-medium text-card-foreground">Pay with Card</p>
              <p className="text-sm text-muted-foreground">Visa, Mastercard, AmEx</p>
              <p className="text-xs text-green-500">No GST</p>
            </div>
            {loading && paymentMethod === 'stripe' && <Loader2 className="h-5 w-5 animate-spin" />}
          </button>

          {/* PayNow */}
          <button
            onClick={() => setPaymentMethod('paynow')}
            className={`w-full p-4 rounded-lg border transition-colors flex items-center gap-4 ${
              paymentMethod === 'paynow' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
            }`}
            data-testid="paynow-btn"
          >
            <div className="h-12 w-12 rounded-lg bg-gradient-to-r from-purple-700 to-pink-600 flex items-center justify-center">
              <QrCode className="h-6 w-6 text-white" />
            </div>
            <div className="text-left flex-1">
              <p className="font-medium text-card-foreground">PayNow</p>
              <p className="text-sm text-muted-foreground">Scan QR with banking app</p>
              <p className="text-xs text-green-500">No GST</p>
            </div>
          </button>

          {/* SCC Account */}
          <button
            onClick={() => setPaymentMethod('scc_account')}
            className={`w-full p-4 rounded-lg border transition-colors flex items-center gap-4 ${
              paymentMethod === 'scc_account' ? 'border-yellow-500 bg-yellow-500/10' : 'border-border hover:border-yellow-500/50'
            }`}
            data-testid="scc-btn"
          >
            <div className="h-12 w-12 rounded-lg bg-gradient-to-r from-amber-600 to-yellow-500 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <div className="text-left flex-1">
              <p className="font-medium text-card-foreground">Bill to SCC Account</p>
              <p className="text-sm text-muted-foreground">Charge to membership account</p>
              <p className="text-xs text-yellow-500">9% GST applies</p>
            </div>
          </button>

          {/* PayNow Details */}
          {paymentMethod === 'paynow' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 bg-charcoal rounded-lg border border-border">
              <div className="text-center mb-4">
                <p className="text-sm text-muted-foreground mb-2">PayNow Details</p>
                <div ref={qrCodeRef} className="inline-block p-4 bg-white rounded-lg">
                  {/* Real PayNow QR Code */}
                  <QRCodeSVG 
                    value={`https://www.paynow.sg/pay?uen=201422384R&amt=${calculatedFees?.grand_total?.toFixed(2) || '0'}&ref=${registrationId || 'ACADEMY'}`}
                    size={160}
                    level="M"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor="#000000"
                  />
                  <p className="text-xs text-gray-500 mt-2">Scan with your bank app</p>
                </div>
                {/* Download Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadQRCode}
                  className="mt-3 text-primary border-primary/50 hover:bg-primary/10"
                  data-testid="academy-download-qr-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Save QR Code
                </Button>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">UEN:</span>
                  <span className="font-mono font-bold">201422384R</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account Name:</span>
                  <span className="text-card-foreground">Football And More Limited</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="text-primary font-bold">${calculatedFees?.grand_total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reference:</span>
                  <span className="font-mono">{registrationId}</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-green-900/20 border border-green-600/30 rounded-lg">
                <p className="text-xs text-green-200">
                  <strong>No GST</strong> - Direct payment to Football And More Limited
                </p>
              </div>
              <div className="mt-4">
                <Label>Transaction Reference (optional)</Label>
                <Input
                  value={transactionReference}
                  onChange={(e) => setTransactionReference(e.target.value)}
                  placeholder="Enter bank reference"
                  className="mt-1"
                />
              </div>
              <Button onClick={handlePayNowConfirm} disabled={loading} className="w-full mt-4 bg-primary">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "I've Made the Payment"}
              </Button>
            </motion.div>
          )}

          {/* SCC Account Details */}
          {paymentMethod === 'scc_account' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 bg-charcoal rounded-lg border border-yellow-600/30">
              <div className="p-4 bg-yellow-900/30 border border-yellow-500/50 rounded-lg mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-yellow-400">9% GST Applies</p>
                    <p className="text-sm text-yellow-200/80">
                      Total with GST: ${(calculatedFees?.grand_total * 1.09).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <Label>SCC Account Number *</Label>
                <Input
                  value={sccAccountNumber}
                  onChange={(e) => setSccAccountNumber(e.target.value)}
                  placeholder="Enter your SCC account number"
                  className="mt-1"
                />
              </div>
              <Button onClick={handleSccBillingConfirm} disabled={loading || !sccAccountNumber} className="w-full mt-4 bg-yellow-600 hover:bg-yellow-700 text-black">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm Billing ($${(calculatedFees?.grand_total * 1.09).toFixed(2)} incl. GST)`}
              </Button>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Step 6: Confirmation
  const renderConfirmation = () => (
    <div className="text-center space-y-6">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success/20">
        <CheckCircle className="h-10 w-10 text-success" />
      </div>
      <div>
        <h2 className="font-display text-2xl text-primary mb-2">Registration Submitted!</h2>
        <p className="text-muted-foreground">
          Welcome to SCC Football Academy!
        </p>
      </div>
      <Card className="bg-gradient-card gold-border text-left">
        <CardContent className="p-6 space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">Registration ID</p>
            <p className="text-lg font-mono text-primary">{registrationId}</p>
          </div>
          {membershipNumbers && membershipNumbers.length > 0 && (
            <div className="p-4 bg-green-900/20 border border-green-600/30 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Academy Membership Number(s)</p>
              <div className="space-y-1">
                {membershipNumbers.map((num, idx) => (
                  <p key={idx} className="text-lg font-mono text-green-400 font-bold" data-testid={`membership-number-${idx}`}>
                    {num}
                  </p>
                ))}
              </div>
              <p className="text-xs text-green-200/70 mt-2">
                Please save these membership numbers for your records
              </p>
            </div>
          )}
          <div>
            <p className="text-sm text-muted-foreground">Status</p>
            <Badge className="bg-yellow-600">
              {paymentStatus === 'pending_verification' && 'Payment Pending Verification'}
              {paymentStatus === 'pending_billing' && 'Billing Pending'}
              {paymentStatus === 'completed' && 'Confirmed'}
            </Badge>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Term</p>
            <p className="text-card-foreground">{selectedTerm?.name}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-primary font-bold">${calculatedFees?.grand_total?.toFixed(2)} SGD</p>
          </div>
        </CardContent>
      </Card>
      <Button onClick={() => window.location.href = '/'} className="bg-primary">
        Back to Home
      </Button>
    </div>
  );

  const stepTitles = ['Term', 'Details', 'Sessions', 'Review', 'Payment', 'Done'];

  return (
    <div className="min-h-screen bg-background" data-testid="academy-registration">
      {/* Header */}
      <div className="bg-charcoal border-b border-border sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground hover:text-primary">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            <div>
              <h1 className="font-display text-xl text-primary">Academy Registration</h1>
              <p className="text-xs text-muted-foreground">SCC Football Academy</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-6 py-4">
        <div className="flex items-center justify-center gap-2 mb-8 overflow-x-auto">
          {stepTitles.map((title, idx) => (
            <React.Fragment key={idx}>
              <div className="flex items-center gap-1 flex-shrink-0">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                  step > idx + 1 ? 'bg-success text-white' : step === idx + 1 ? 'bg-primary text-primary-foreground' : 'bg-charcoal text-muted-foreground'
                }`}>
                  {step > idx + 1 ? <CheckCircle className="h-4 w-4" /> : idx + 1}
                </div>
                <span className={`text-xs hidden sm:block ${step >= idx + 1 ? 'text-primary' : 'text-muted-foreground'}`}>
                  {title}
                </span>
              </div>
              {idx < stepTitles.length - 1 && (
                <ChevronRight className={`h-4 w-4 flex-shrink-0 ${step > idx + 1 ? 'text-success' : 'text-charcoal'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 pb-12">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          {step === 1 && renderTermSelection()}
          {step === 2 && renderDetailsForm()}
          {step === 3 && renderSessionSelection()}
          {step === 4 && renderReview()}
          {step === 5 && renderPayment()}
          {step === 6 && renderConfirmation()}
        </motion.div>

        {error && (
          <div className="flex items-center gap-2 text-destructive bg-destructive/10 p-3 rounded-lg mt-4">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Navigation Buttons */}
        {step < 5 && (
          <div className="flex gap-4 mt-6">
            {step > 1 && (
              <Button variant="outline" onClick={prevStep} className="flex-1 gold-border">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            <Button
              onClick={step === 4 ? handleSubmitRegistration : nextStep}
              disabled={loading}
              data-testid="academy-continue-btn"
              className="flex-1 bg-primary"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>
                  {step === 4 ? 'Continue to Payment' : 'Continue'}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademyRegistration;
